import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Button,
    Chip,
    IconButton,
    useTheme,
    alpha,
    Popover,
    Paper,
    LinearProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    Circle as CircleIcon,
    CalendarToday as CalendarIcon
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../../api/api';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useNavigate } from 'react-router-dom';
import PatientDetailsDialog from './PatientDetailsDialog';
import en_ja from '../../en-ja-map';

const BookingManagement = () => {
    // Date state management
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
    const [appointmentList, setApointmentList] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarType, setSnackbarType] = useState('success')
    const [loading, setLoading] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const calendarButtonRef = useRef(null);
    const user = JSON.parse(localStorage.getItem('user'))
    const navigate = useNavigate()
    const [inPast, setInPast] = useState(false)
    const parseTime = (timeStr) => new Date(`1970-01-01T${timeStr}Z`);
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [selectedPatientData, setSelectedPatientData] = useState(null);
    const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [appointmentToDelete, setAppointmentToDelete] = useState(null);

    const getMyDate = (myDate) => {
        const date = new Date(myDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        return formattedDate;
    }

    const isSameDate = (date1, date2) => {
        if (!date1 || !date2) return false;
        return date1.toDateString() === date2.toDateString();
    };

    const handlePatientClick = (appointment) => {
        setSelectedPatientData(appointment);
        setPatientDialogOpen(true);
    };

    const isPastDate = (selectedDateStr, storedDateStr) => {
        const selected = new Date(selectedDateStr);
        const stored = new Date(storedDateStr);
        selected.setHours(0, 0, 0, 0);
        stored.setHours(0, 0, 0, 0);
        return selected < stored;
    }

    const isFutureAppointment = (appointmentDate, appointmentTime) => {
        const myDate = getMyDate(new Date(appointmentDate));
        const dateTimeString = `${myDate}T${appointmentTime}`;
        const slotDateTime = new Date(dateTimeString);
        return slotDateTime > currentTime;
    }

    const getAppointmentData = async () => {
        const today = getMyDate(new Date())
        const formattedDate = getMyDate(selectedDate);
        setInPast(isPastDate(formattedDate, today));
        setLoading(true);
        api.get(`/v1/clinic/clinics/${user.id}/appointments?date=${formattedDate}`)
            .then((res) => {
                if (res?.data?.message) {
                    setApointmentList([])
                } else {
                    const data = res?.data?.data;
                    data.sort((a, b) =>
                        parseTime(a.appointment_time) - parseTime(b.appointment_time)
                    );
                    const finalData = data.reduce((acc, item) => {
                        const key = item.appointment_time;
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(item);
                        return acc;
                    }, {});
                    setApointmentList(finalData);
                    if (!isToday) {
                        setSnackbarMessage((en_ja['Bookings for {date}'] || 'Bookings for {date}').replace('{date}', formattedDate));
                        setSnackbarType('success');
                        setSnackbarOpen(true);
                    }
                }
                setLoading(false)
            })
            .catch((err) => {
                console.error(err)
                setLoading(false)
            })
    }

    useEffect(() => {
        getAppointmentData();
    }, [selectedDate])

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    const checkTime = (date, time) => {
        const myDate = getMyDate(date)
        const dateTimeString = `${myDate}T${time}`;
        const slotDateTime = new Date(dateTimeString);
        const fiveMinutesBefore = new Date(slotDateTime.getTime() - 5 * 60 * 1000);
        const timeUp = new Date(slotDateTime.getTime() + 55 * 60 * 1000);
        const isJoin = currentTime >= fiveMinutesBefore && currentTime <= timeUp;
        return isJoin;
    }

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0"); // month is 0-indexed
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}/${month}/${day}`;
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const handleDeleteClick = (appointment) => {
        setAppointmentToDelete(appointment);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (appointmentToDelete) {
            const obj = {
                appointmentId: appointmentToDelete.id,
                status: "deleted",
                clinicId: user.id,
                userId: appointmentToDelete.user_id,
                clinic_slot_id: appointmentToDelete.clinic_slot_id
            }
            api.put("/v1/clinic/appointments/status", obj)
                .then((res) => {
                    setSnackbarMessage(en_ja['Appointment deleted successfully'] || 'Appointment deleted successfully')
                    setSnackbarType('success')
                    setSnackbarOpen(true)
                    getAppointmentData();
                })
                .catch((err) => {
                    console.error(err)
                    setSnackbarMessage(en_ja['Failed to delete appointment'] || 'Failed to delete appointment')
                    setSnackbarType('error')
                    setSnackbarOpen(true)
                })
        }
        setDeleteDialogOpen(false);
        setAppointmentToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAppointmentToDelete(null);
    };

    // Calendar handlers
    const handleCalendarClick = (event) => {
        setCalendarAnchorEl(event.currentTarget);
    };
    const handleCalendarClose = () => setCalendarAnchorEl(null);

    const handleDateSelect = (selectInfo) => {
        const newSelectedDate = new Date(selectInfo.start);
        setSelectedDate(newSelectedDate);
        setSelectedCalendarDate(newSelectedDate);
        handleCalendarClose();
    };

    const getDayCellClassNames = (arg) => {
        const cellDate = new Date(arg.date);
        const today = new Date();
        if (selectedCalendarDate &&
            isSameDate(cellDate, selectedCalendarDate) &&
            !isSameDate(cellDate, today)) {
            return ['fc-day-selected-custom'];
        }
        return [];
    };

    const handleTodayClick = () => {
        const today = new Date();
        setSelectedDate(today);
        setSelectedCalendarDate(null);
    };

    const open = Boolean(calendarAnchorEl);

    const handleAccept = (appointmentId, userId, clinic_slot_id) => {
        const obj = {
            appointmentId,
            status: "confirmed",
            clinicId: user.id,
            userId,
            clinic_slot_id
        }
        api.put("/v1/clinic/appointments/status", obj)
            .then((res) => {
                setSnackbarMessage(res?.data?.message)
                setSnackbarType('success')
                setSnackbarOpen(true)
                getAppointmentData();
            })
            .catch((err) => {
                console.error(err)
                getAppointmentData();
            })
    };

    const handleDecline = (appointmentId, userId, clinic_slot_id) => {
        const obj = {
            appointmentId,
            status: "cancelled",
            clinicId: user.id,
            userId,
            clinic_slot_id
        }
        api.put("/v1/clinic/appointments/status", obj)
            .then((res) => {
                setSnackbarMessage(res?.data?.message)
                setSnackbarType('success')
                setSnackbarOpen(true)
            })
            .catch((err) => {
                console.error(err)
            })
        getAppointmentData();
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false)
        setSnackbarMessage('')
    }

    const handlePrescription = (appointment) => {
        navigate('/prescription-management', { state: { appointment } })
    }

    const joinCall = (agora_session, patientName, appointmentId, userId, clinic_checkin_data) => {
        navigate('/connect-call', {
            state: {
                clinicId: user.id,
                channelName: agora_session.channel_name,
                clinicToken: agora_session.clinic_token,
                patientName,
                appointmentId,
                userId,
                clinic_checkin_data
            }
        })
    }

    const PatientRow = ({ appointment, timeSlot, index }) => {
        const isOnline = appointment.appointment_type === 'online';
        const isFuture = isFutureAppointment(appointment.appointment_date, appointment.appointment_time);
        const showDeleteButton = isFuture && appointment.status === 'confirmed';
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2.5,
                    borderBottom: index < 3 ? '1px solid #f0f0f0' : 'none',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: alpha('#000', 0.02),
                        transform: 'translateX(4px)',
                        '& .patient-avatar': { transform: 'scale(1.05)' }
                    }
                }}
            >
                {/* Index */}
                <Typography
                    variant="body2"
                    sx={{ minWidth: '30px', color: '#666', fontWeight: 600, mr: 2.5, fontSize: '0.875rem' }}
                >
                    {index + 1}
                </Typography>

                {/* Patient Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => handlePatientClick(appointment)}>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.3, fontSize: '0.875rem', cursor: 'pointer', '&:hover': { color: '#dc3545' } }}
                        >
                            {appointment?.user?.name || "Test User"}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 500 }}
                        >
                            {(en_ja['Patient Id:'] || 'Patient Id:')} {appointment?.user?.id}
                        </Typography>
                    </Box>
                </Box>

                {/* Email */}
                <Box sx={{ flex: 1.8, minWidth: 0, mx: 1.5 }}>
                    <Typography
                        variant="body2"
                        onClick={() => handlePatientClick(appointment)}
                        sx={{ color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem' }}
                    >
                        {appointment?.user?.email}
                    </Typography>
                </Box>

                {/* Contact */}
                <Box sx={{ flex: 1, mx: 1.5 }}>
                    <Typography
                        variant="body2"
                        onClick={() => handlePatientClick(appointment)}
                        sx={{ color: '#444', fontSize: '0.875rem' }}
                    >
                        {appointment?.user?.phone_number}
                    </Typography>
                </Box>

                {/* Consult Type */}
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mx: 1.5 }} onClick={() => handlePatientClick(appointment)}>
                    <CircleIcon sx={{ fontSize: 8, color: isOnline ? '#4CAF50' : '#FF9800', mr: 1 }} />
                    <Typography
                        variant="body2"
                        sx={{ color: isOnline ? '#4CAF50' : '#FF9800', fontWeight: 600, fontSize: '0.875rem', textTransform: 'capitalize' }}
                    >
                        {en_ja[appointment.appointment_type]}
                    </Typography>
                </Box>

                {/* Actions/Status */}
                <Box sx={{ flex: 1.2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    {appointment.status === 'pending' && (
                        <>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleAccept(appointment.id, appointment.user_id, appointment.clinic_slot_id)}
                                disabled={inPast}
                                sx={{
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    px: 2.5, py: 0.7, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                                    borderRadius: '8px', minWidth: '70px', transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.2)',
                                    '&:hover': { backgroundColor: '#45a049', transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)' }
                                }}
                            >
                                {en_ja['Accept'] || 'Accept'}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={inPast}
                                onClick={() => handleDecline(appointment.id, appointment.user_id, appointment.clinic_slot_id)}
                                sx={{
                                    borderColor: '#f44336', color: '#f44336',
                                    px: 2.5, py: 0.7, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                                    borderRadius: '8px', minWidth: '70px', transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: '#f44336', color: 'white', transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(244, 67, 54, 0.4)' }
                                }}
                            >
                                {en_ja['Decline'] || 'Decline'}
                            </Button>
                        </>
                    )}
                    {appointment.status === 'auto-approved' && (
                        <Chip
                            label={en_ja['Auto Approved'] || 'Auto Approved'}
                            size="small"
                            sx={{ backgroundColor: '#E3F2FD', color: '#1976D2', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px' }}
                        />
                    )}
                    {appointment.status === 'completed' && (
                        <>
                            {/* <Chip
                                label={en_ja['Appointment Completed'] || 'Appointment Completed'}
                                size="small"
                                sx={{ backgroundColor: '#E8F5E8', color: '#4CAF50', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px', cursor: 'default' }}
                            /> */}
                            <Chip
                                label={en_ja['Create Prescription'] || 'Create Prescription'}
                                size="small"
                                onClick={() => handlePrescription(appointment)}
                                sx={{ backgroundColor: '#E3F2FD', color: '#1976D2', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px' }}
                            />
                        </>
                    )}
                    {appointment.status === 'closed' && (
                        <>
                            <Chip
                                label={en_ja['Appointment Completed'] || 'Appointment Completed'}
                                size="small"
                                sx={{ backgroundColor: '#E8F5E8', color: '#4CAF50', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px', cursor: 'default' }}
                            />
                        </>
                    )}
                    {!checkTime(appointment.appointment_date, appointment.appointment_time) && appointment.status === 'confirmed' && (
                        <>
                            <Chip
                                label={en_ja['Accepted'] || 'Accepted'}
                                size="small"
                                sx={{ backgroundColor: '#E8F5E8', color: '#4CAF50', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px' }}
                            />
                            {showDeleteButton}
                            {showDeleteButton && (
                                <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick(appointment)}
                                    sx={{
                                        color: '#f44336',
                                        ml: 0.5,
                                        padding: '4px',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                            transform: 'scale(1.1)',
                                        }
                                    }}
                                >
                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            )}
                        </>
                    )}
                    {checkTime(appointment.appointment_date, appointment.appointment_time) && appointment.status === 'confirmed' && (
                        <Chip
                            label={en_ja['Join Call'] || 'Join Call'}
                            size="small"
                            onClick={() => joinCall(appointment.agora_session, appointment.user.name, appointment.id, appointment.user.id, appointment.clinic_checkin_data)}
                            sx={{ backgroundColor: '#c0dbff', color: '#4c60afff', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px' }}
                        />
                    )}
                    {appointment.status === 'cancelled' && (
                        <Chip
                            label={en_ja['Cancelled'] || 'Cancelled'}
                            size="small"
                            sx={{ backgroundColor: '#FFEBEE', color: '#f44336', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px' }}
                        />
                    )}
                    {appointment.status === 'deleted' && (
                        <Chip
                            label={en_ja['Deleted'] || 'Deleted'}
                            size="small"
                            sx={{ backgroundColor: '#FFEBEE', color: '#f44336', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px' }}
                        />
                    )}
                    {appointment.status === 'rescheduled' && (
                        <Chip
                            label={en_ja['Rescheduled'] || 'Rescheduled'}
                            size="small"
                            sx={{ backgroundColor: '#FFEBEE', color: '#f44336', fontWeight: 600, fontSize: '0.75rem', borderRadius: '8px', height: '28px' }}
                        />
                    )}
                </Box>
            </Box>
        );
    };

    const TimeSlotCard = ({ timeSlot, appointments }) => {
        return (
            <Card
                sx={{
                    mb: 3,
                    borderRadius: '16px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.12)', transform: 'translateY(-4px)', '& .card-header': { backgroundColor: '#f1f3f4' } }
                }}
            >
                {/* Header */}
                <Box
                    className="card-header"
                    sx={{
                        backgroundColor: '#f8f9fa',
                        px: 3,
                        py: 2.5,
                        borderBottom: '1px solid #e9ecef',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'background-color 0.2s ease'
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.1rem', letterSpacing: '-0.02em' }}>
                        {timeSlot}
                    </Typography>
                </Box>

                {/* Table Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 2,
                        px: 2.5,
                        backgroundColor: '#fafbfc',
                        borderBottom: '2px solid #e9ecef'
                    }}
                >
                    <Typography variant="caption" sx={{ minWidth: '30px', fontWeight: 700, color: '#666', mr: 2.5, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        #
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {en_ja['Name'] || 'Name'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1.8, mx: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {en_ja['Email'] || 'Email'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1, mx: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {en_ja['Contact'] || 'Contact'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1, mx: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {en_ja['Consult Type'] || 'Consult Type'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1.2, textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {en_ja['Actions'] || 'Actions'}
                        </Typography>
                    </Box>
                </Box>

                {/* Patient Rows */}
                <CardContent sx={{ p: 0 }}>
                    {appointments.map((appointment, index) => (
                        <PatientRow key={appointment.id} appointment={appointment} timeSlot={timeSlot} index={index} />
                    ))}
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <CustomSnackbar
                isOpen={snackbarOpen}
                message={snackbarMessage}
                type={snackbarType}
                onClose={handleCloseSnackbar}
            />
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: '1px solid #e9ecef' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '2.5rem', letterSpacing: '-0.02em' }}>
                            {formatDate(selectedDate)}
                        </Typography>
                        {!isToday(selectedDate) && (
                            <Typography
                                variant="body2"
                                onClick={handleTodayClick}
                                sx={{ color: '#888', fontSize: '0.9rem', cursor: 'pointer', transition: 'color 0.2s ease', '&:hover': { color: '#dc3545', textDecoration: 'underline' } }}
                            >
                                {`- ${(en_ja['Today'] || 'Today')}`}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        ref={calendarButtonRef}
                        onClick={handleCalendarClick}
                        sx={{
                            backgroundColor: 'white',
                            border: '2px solid #e9ecef',
                            width: 48, height: 48, transition: 'all 0.2s ease',
                            '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#dc3545', transform: 'scale(1.05)', '& .MuiSvgIcon-root': { color: '#dc3545' } }
                        }}
                    >
                        <CalendarIcon sx={{ color: '#666', transition: 'color 0.2s ease' }} />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ width: '100%', mb: 3 }}>
                {loading && <LinearProgress />}
            </Box>

            {/* Calendar Popover */}
            <Popover
                open={open}
                anchorEl={calendarAnchorEl}
                onClose={handleCalendarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{
                    mt: 1,
                    '& .MuiPopover-paper': {
                        borderRadius: '16px',
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 8px 32px rgba(0,0,0,0.08))',
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: -8,
                            left: '50%',
                            width: 16, height: 16,
                            backgroundColor: 'white',
                            transform: 'translateX(-50%) rotate(45deg)',
                            zIndex: 0,
                        }
                    }
                }}
            >
                <Paper
                    sx={{
                        p: 0,
                        width: 380,
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)',
                        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)'
                    }}
                >
                    <Box sx={{
                        '& .fc': { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontSize: '14px', lineHeight: 1.4 },
                        '& .fc-header-toolbar': { padding: '20px 24px 16px', margin: 0, backgroundColor: 'white', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
                        '& .fc-toolbar-title': { fontSize: '20px', fontWeight: 700, color: '#1a1a1a', margin: 0, letterSpacing: '-0.02em' },
                        '& .fc-button-group': { display: 'flex', gap: '8px' },
                        '& .fc-button': {
                            backgroundColor: 'transparent', border: 'none', color: '#6b7280', padding: '8px', borderRadius: '8px', fontSize: '16px', fontWeight: 500,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', minWidth: 'auto', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            '&:hover': { backgroundColor: '#f3f4f6', color: '#1f2937', transform: 'scale(1.05)' }, '&:focus': { boxShadow: 'none', outline: 'none' }, '&:active': { transform: 'scale(0.98)' }
                        },
                        '& .fc-today-button': {
                            backgroundColor: '#dc3545', color: 'white', fontSize: '13px', fontWeight: 600, padding: '6px 12px',
                            borderRadius: '8px', width: 'auto', minWidth: '60px',
                            '&:hover': { backgroundColor: '#c82333', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)' }
                        },
                        '& .fc-daygrid': { padding: '0 24px 20px' },
                        '& .fc-col-header': { backgroundColor: 'transparent', borderBottom: 'none', padding: '16px 0 12px' },
                        '& .fc-col-header-cell': { border: 'none', padding: 0 },
                        '& .fc-col-header-cell-cushion': { color: '#9ca3af', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px', padding: 0, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
                        '& .fc-daygrid-day': { border: 'none', position: 'relative', padding: '2px' },
                        '& .fc-daygrid-day-frame': { minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' },
                        '& .fc-daygrid-day-number': {
                            color: '#374151', fontSize: '15px', fontWeight: 500, textDecoration: 'none', width: '40px', height: '40px', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', borderRadius: '12px', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', zIndex: 1,
                            '&:hover': { backgroundColor: '#f8fafc', color: '#1f2937', transform: 'scale(1.05)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }
                        },
                        '& .fc-day-today': {
                            backgroundColor: 'transparent',
                            '& .fc-daygrid-day-number': {
                                backgroundColor: '#dc3545', color: 'white', fontWeight: 700,
                                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.4)',
                                '&:hover': { backgroundColor: '#c82333', transform: 'scale(1.1)', boxShadow: '0 6px 16px rgba(220, 53, 69, 0.5)' }
                            }
                        },
                        '& .fc-day-selected': { '& .fc-daygrid-day-number': { backgroundColor: '#1f2937', color: 'white', fontWeight: 600, boxShadow: '0 4px 12px rgba(31, 41, 55, 0.3)' } },
                        '& .fc-day.fc-day-selected-custom .fc-daygrid-day-number': {
                            backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, boxShadow: '0 2px 6px rgba(21, 101, 192, 0.2)',
                            '&:hover': { backgroundColor: '#bbdefb', transform: 'scale(1.05)', boxShadow: '0 3px 10px rgba(21, 101, 192, 0.3)' }
                        },
                        '& .fc-day-sat:not(.fc-day-today) .fc-daygrid-day-number, & .fc-day-sun:not(.fc-day-today) .fc-daygrid-day-number': {
                            color: '#dc3545', '&:hover': { backgroundColor: '#fef2f2', color: '#dc2626' },
                        },
                        '& .fc-day-other .fc-daygrid-day-number': { color: '#d1d5db', '&:hover': { backgroundColor: '#f9fafb', color: '#9ca3af' } },
                        '& .fc-scrollgrid': { border: 'none' },
                        '& .fc-scrollgrid-section > *': { border: 'none' },
                        '& .fc-scrollgrid-section-header > *': { borderBottom: 'none' },
                        '& .fc-scrollgrid-section-body > *': { border: 'none' },
                        '& .fc-daygrid-week:hover .fc-daygrid-day': { backgroundColor: 'rgba(248, 250, 252, 0.5)' },
                        '& .fc-daygrid-day-events': { display: 'none' },
                    }}>
                        <FullCalendar
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            selectable={true}
                            selectMirror={true}
                            dayMaxEvents={false}
                            weekends={true}
                            select={handleDateSelect}
                            initialDate={selectedDate}
                            dayCellClassNames={getDayCellClassNames}
                            headerToolbar={{ left: 'prev', center: 'title', right: 'next today' }}
                            height="auto"
                            aspectRatio={1.4}
                            buttonText={{ today: (en_ja['Today'] || 'Today') }}
                            buttonIcons={{ prev: 'chevron-left', next: 'chevron-right' }}
                            dayHeaderFormat={{ weekday: 'short' }}
                            titleFormat={{ year: 'numeric', month: 'long' }}
                            fixedWeekCount={false}
                            showNonCurrentDates={true}
                            dayMaxEventRows={false}
                            eventDisplay="block"
                            displayEventTime={false}
                        />
                    </Box>
                </Paper>
            </Popover>

            {/* Time Slot Cards */}
            {!loading && appointmentList && Object.entries(appointmentList).map(([timeSlot, appointments]) => (
                <TimeSlotCard key={timeSlot} timeSlot={timeSlot} appointments={appointments} />
            ))}

            {!loading && Object.entries(appointmentList).length === 0 &&
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8, px: 4, mt: 4,
                        backgroundColor: '#fafbfc',
                        borderRadius: '16px',
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease'
                    }}
                >
                    <Box sx={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: 'rgba(220, 53, 69, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.05)' } }}>
                        <CalendarIcon sx={{ fontSize: 40, color: '#dc3545', opacity: 0.8 }} />
                    </Box>

                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1, textAlign: 'center', fontSize: '1.5rem', letterSpacing: '-0.02em' }}>
                        {en_ja['No Bookings Found'] || 'No Bookings Found'}
                    </Typography>

                    <Typography variant="body1" sx={{ color: '#6b7280', textAlign: 'center', mb: 4, maxWidth: '400px', lineHeight: 1.6 }}>
                        {inPast
                            ? (en_ja['No appointments were scheduled for {date}'] || 'No appointments were scheduled for {date}').replace('{date}', formatDate(selectedDate))
                            : (en_ja['No appointments scheduled for {date}.'] || 'No appointments scheduled for {date}.').replace('{date}', formatDate(selectedDate))
                        }
                    </Typography>

                    {inPast && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>
                                {en_ja['Try selecting a different date or'] || 'Try selecting a different date or'}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={handleTodayClick}
                                sx={{
                                    borderColor: '#dc3545', color: '#dc3545',
                                    px: 3, py: 1, fontWeight: 600, fontSize: '0.9rem', textTransform: 'none', borderRadius: '8px',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: '#dc3545', color: 'white', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)' }
                                }}
                            >
                                {en_ja['Go to Today'] || 'Go to Today'}
                            </Button>
                        </Box>
                    )}
                </Box>
            }

            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => setPatientDialogOpen(false)}
                patientData={selectedPatientData?.user}
                appointmentData={selectedPatientData}
            />
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        padding: '8px'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', color: '#1a1a1a' }}>
                    {en_ja['Confirm Delete'] || 'Confirm Delete'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#666', fontSize: '1rem' }}>
                        {en_ja['Are you sure you want to delete this appointment? This action cannot be undone.'] || 'Are you sure you want to delete this appointment? This action cannot be undone.'}
                    </DialogContentText>
                    {appointmentToDelete && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                            <Typography variant="body2" sx={{ color: '#444', mb: 0.5 }}>
                                <strong>{en_ja['Patient'] || 'Patient'}</strong>: {appointmentToDelete?.user?.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#444', mb: 0.5 }}>
                                <strong>{en_ja['Date:'] || 'Date:'}</strong>{getMyDate(appointmentToDelete?.appointment_date)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#444' }}>
                                <strong>{en_ja['Time'] || 'Time'}</strong>: {appointmentToDelete?.appointment_time}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button
                        onClick={handleDeleteCancel}
                        sx={{
                            color: '#666',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        {en_ja['Cancel'] || 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f44336',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: '#d32f2f' }
                        }}
                    >
                        {en_ja['Delete'] || 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BookingManagement;